/**
 * @description Hooks for managing connections and credentials
 * This file contains hooks for creating credentials and updating tenant settings
 * when adding new connections.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAppStore } from '@/store/appStore';
import {
  createNewCredential,
  patchCredential,
  patchCredentialStatus,
  deleteCredential,
  fetchSlackLink,
  linkSlackWorkspace,
  unlinkSlackWorkspace,
  fetchAllCredential,
  getTenantDetails,
  updateTenantSettings,
  createDataset,
  startConnectCardSync,
  deleteDataset
} from '@/services/DataService';
import { logger } from "@/lib/logger";
import { openOAuthWindowForConnection, whenPopupCloses } from '@/hooks/useOauth';

import { ApiKeyCredentials, ApiKeyPayload, BasicAuthCredentials, CreateApiKeyConnectionParams, CreateBasicAuthConnectionParams, CreateConnectionParams, CredentialStatus, OAuthClientCredentials, OAuthClientPayload, OauthCredentials, OauthPayload, ReauthorizeConnectionParams, TenantSettings } from '@/types/connections';

// Hook to get tenant details
export const useTenantDetails = (options?: { enabled?: boolean }) => {
  const { isSignedIn } = useAuth();
  const enabled = Boolean(isSignedIn && (options?.enabled ?? true));

  return useQuery({
    queryKey: ['tenant-details'],
    queryFn: getTenantDetails,
    enabled,
    // Override global setting to refetch when window regains focus
    // Critical for updating UI state after user returns from idle
    refetchOnWindowFocus: true,
  });
};

// Hook to fetch credentials
export const useCredentials = () => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: ['credentials'],
    queryFn: fetchAllCredential,
    enabled: isSignedIn,
  });
};

// Hook to create credential (accepts OAuth or basic-auth payload)
export const useCreateCredential = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: OauthCredentials | BasicAuthCredentials | ApiKeyCredentials | OAuthClientCredentials) => createNewCredential(payload),
    onSuccess: () => {
      // Invalidate both credentials and tenant details to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Hook to pause or resume a credential
export const useUpdateCredentialStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ credentialId, status }: { credentialId: string; status: CredentialStatus }) =>
      patchCredentialStatus(credentialId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Hook to delete (disconnect) a credential. Server revokes the upstream OAuth
// grant before destroying the row, so disconnect is a one-call operation.
export const useDeleteCredential = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentialId: string) => deleteCredential(credentialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Hook to read which Slack workspaces are linked to the tenant (i.e. routing
// inbound bot events to it — separate from just having a Slack credential).
export const useSlackLink = () => {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: ['slack-link'],
    queryFn: fetchSlackLink,
    enabled: isSignedIn,
  });
};

// Hook to explicitly link the tenant's connected Slack workspace.
export const useLinkSlackWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => linkSlackWorkspace(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-link'] });
    },
  });
};

// Hook to unlink a Slack workspace from the tenant.
export const useUnlinkSlackWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: string) => unlinkSlackWorkspace(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-link'] });
    },
  });
};

// Hook to update tenant settings
export const useUpdateTenantSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: TenantSettings) => updateTenantSettings(settings),
    onSuccess: () => {
      // Invalidate both credentials and tenant details to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Legacy pointer some workflow $refs still read (settings.<app>.credential_id).
// Written only when the app gains its FIRST connection — a later connection
// must not steal it. The server-authoritative default is credential.is_default.
const useWriteFirstConnectSettings = () => {
  const updateTenantSettingsMutation = useUpdateTenantSettings();
  const { data: tenantData } = useTenantDetails();
  const { data: credentialsData } = useCredentials();

  return async (appName: string, credentialId: string): Promise<TenantSettings> => {
    const currentSettings: TenantSettings = tenantData?.tenant?.settings || {};
    const alreadyConnected = credentialsData?.credentials?.some(
      (credential) => credential.app_name === appName
    );

    if (alreadyConnected) {
      return currentSettings;
    }

    const updatedSettings = {
      ...currentSettings,
      [appName]: {
        credential_id: credentialId
      }
    };

    await updateTenantSettingsMutation.mutateAsync(updatedSettings);

    return updatedSettings;
  };
};

// Combined hook to create connection (credential + first-connect settings)
export const useCreateConnection = () => {
  const createCredentialMutation = useCreateCredential();
  const writeFirstConnectSettings = useWriteFirstConnectSettings();

  return useMutation({
    mutationFn: async ({ oauthPayload, appName }: CreateConnectionParams) => {
      const credentialResponse = await createCredentialMutation.mutateAsync({
        credential: oauthPayload
      });

      const settings = await writeFirstConnectSettings(
        appName, credentialResponse.credential.id
      );

      return {
        credential: credentialResponse.credential,
        settings
      };
    },
  });
};

// Combined hook to create a basic-auth connection. Mirrors useCreateConnection.
export const useCreateBasicAuthConnection = () => {
  const createCredentialMutation = useCreateCredential();
  const writeFirstConnectSettings = useWriteFirstConnectSettings();

  return useMutation({
    mutationFn: async ({ payload, appName }: CreateBasicAuthConnectionParams) => {
      const credentialResponse = await createCredentialMutation.mutateAsync({
        credential: payload
      });

      const settings = await writeFirstConnectSettings(
        appName, credentialResponse.credential.id
      );

      return {
        credential: credentialResponse.credential,
        settings
      };
    },
  });
};

// Combined hook to create an api-key connection. Mirrors useCreateConnection.
export const useCreateApiKeyConnection = () => {
  const createCredentialMutation = useCreateCredential();
  const writeFirstConnectSettings = useWriteFirstConnectSettings();

  return useMutation({
    mutationFn: async ({ payload, appName }: CreateApiKeyConnectionParams) => {
      const credentialResponse = await createCredentialMutation.mutateAsync({
        credential: payload
      });

      const settings = await writeFirstConnectSettings(
        appName, credentialResponse.credential.id
      );

      return {
        credential: credentialResponse.credential,
        settings
      };
    },
  });
};

/**
 * Connects an app whose OAuth client belongs to the customer (Salesforce).
 *
 * Two steps, in this order, because the client pair is what the authorize and
 * token calls are made WITH:
 *  1. create the connection record holding the pair and no tokens
 *  2. open a handshake against it, and authorize in the popup
 *
 * The callback writes the tokens onto that same record server-side, so unlike
 * every other OAuth app nothing is posted back from the browser afterwards.
 */
export const useCreateOAuthClientConnection = () => {
  const createCredentialMutation = useCreateCredential();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: OAuthClientPayload) => {
      const credentialResponse = await createCredentialMutation.mutateAsync({
        credential: payload
      });

      const popup = openOAuthWindowForConnection(
        payload.app_name, payload.auth_name, credentialResponse.credential.id
      );

      // The authorization writes the tokens server-side, so unlike every other
      // OAuth app nothing comes back through the browser to invalidate the
      // cache. Without this the list keeps showing the connection as it was
      // created — paused, pending authorization — until a manual reload.
      whenPopupCloses(popup, () => {
        queryClient.invalidateQueries({ queryKey: ['credentials'] });
        queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
      });

      return { credential: credentialResponse.credential, popup };
    },
  });
};

/**
 * Authorizes a connection that already exists — the second half of the flow
 * above, on its own, for a record left pending because the popup was closed or
 * the consent screen was abandoned.
 */
export const useAuthorizeConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      { credentialId, appName, authName }:
      { credentialId: string; appName: string; authName?: string }
    ) => {
      const popup = openOAuthWindowForConnection(appName, authName || 'default', credentialId);

      whenPopupCloses(popup, () => {
        queryClient.invalidateQueries({ queryKey: ['credentials'] });
        queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
      });

      return popup;
    },
  });
};

// Hook to mark a connection as the app's default (server clears the sibling).
export const useSetDefaultCredential = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentialId: string) =>
      patchCredential(credentialId, { credential: { is_default: true } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Hook to rename a connection's display label.
export const useRenameCredential = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ credentialId, displayName }: { credentialId: string; displayName: string }) =>
      patchCredential(credentialId, { credential: { display_name: displayName } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
    },
  });
};

// Hook to patch credential
export const usePatchCredential = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ credentialId, payload }: { credentialId: string; payload: OauthCredentials }) => 
      patchCredential(credentialId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Hook to update an existing api-key credential (re-enter / rotate the key).
export const useUpdateApiKeyConnection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ credentialId, payload }: { credentialId: string; payload: ApiKeyPayload }) =>
      patchCredential(credentialId, { credential: payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Hook to patch credential custom_data only (reuses patchCredential)
export const usePatchCredentialCustomData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ credentialId, customData }: { credentialId: string; customData: Record<string, unknown> }) =>
      patchCredential(credentialId, { credential: { custom_data: customData } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-details'] });
    },
  });
};

// Combined hook to reauthorize connection (patch credential + tenant settings update)
export const useReauthorizeConnection = () => {
  const patchCredentialMutation = usePatchCredential();
  const updateTenantSettingsMutation = useUpdateTenantSettings();
  const { data: tenantData } = useTenantDetails();

  return useMutation({
    mutationFn: async ({ oauthPayload, credentialId }: ReauthorizeConnectionParams) => {
      // Step 1: Patch credential
      const credentialResponse = await patchCredentialMutation.mutateAsync({
        credentialId,
        payload: { credential: oauthPayload }
      });

      // Step 2: Backfill the legacy settings pointer only when the app has
      // none — reauthorizing a non-default connection must not steal it.
      const currentSettings = tenantData?.tenant?.settings || {};
      const appName = oauthPayload.app_name;

      if (currentSettings[appName]?.credential_id) {
        return {
          credential: credentialResponse.credential,
          settings: currentSettings
        };
      }

      const updatedSettings = {
        ...currentSettings,
        [appName]: {
          credential_id: credentialId
        }
      };

      await updateTenantSettingsMutation.mutateAsync(updatedSettings);

      return {
        credential: credentialResponse.credential,
        settings: updatedSettings
      };
    },
  });
};

// Direct reauthorization hook - handles OAuth flow and credential patching without modal
// This is exclusively done to avoid 2 click actions for reauthorizing a connection
export const useDirectReauthorization = () => {
  const reauthorizeConnectionMutation = useReauthorizeConnection();
  const accessToken = useAppStore((state) => state.authDetails.accessToken);
  const refreshToken = useAppStore((state) => state.authDetails.refreshToken);
  const userId = useAppStore((state) => state.authDetails.userId);
  const userName = useAppStore((state) => state.authDetails.userName);
  const userEmail = useAppStore((state) => state.authDetails.userEmail);
  const setAuthDetails = useAppStore((state) => state.setAuthDetails);

  const prepareOAuthPayload = (appName: string, displayName: string): OauthPayload => ({
    app_name: appName,
    display_name: displayName,
    auth_name: "default",
    token_type: "oauth",
    access_token: accessToken as string,
    refresh_token: refreshToken as string,
    principal_id: userId as string,
    principal_name: userName as string,
    principal_email: userEmail as string,
  });

  const resetAuthDetails = () => {
    setAuthDetails({
      userId: '',
      userName: '',
      userEmail: '',
      accessToken: '',
      refreshToken: '',
    });
  };

  const handleReauthorization = async ({ credentialId, appName, displayName }: { credentialId: string; appName: string; displayName: string }) => {
    try {
      const oauthPayload = prepareOAuthPayload(appName, displayName);
      await reauthorizeConnectionMutation.mutateAsync({
        oauthPayload,
        credentialId
      });
    } catch (error) {
      logger(error as Error, { context: 'Error handling reauthorization' });
      throw error;
    } finally {
      resetAuthDetails();
    }
  };

  return {
    reauthorize: handleReauthorization,
    isPending: reauthorizeConnectionMutation.isPending,
  };
};

/**
 * Hook to create a dataset for a credential.
 */
export const useCreateDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      credentialId,
      displayName,
    }: {
      credentialId: string;
      displayName: string;
    }) => {
      return createDataset(credentialId, displayName);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['credentials'],
      });
    },
  });
};

/**
 * Hook to start (or resume) a Fivetran Connect Card warehouse sync.
 */
export const useStartConnectCardSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appName,
      displayName,
    }: {
      appName: string;
      displayName: string;
    }) => {
      return startConnectCardSync(appName, displayName);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['credentials'],
      });
    },
  });
};

/**
 * Hook to delete a dataset for a credential.
 */
export const useDeleteDataset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      credentialId,
      datasetId,
    }: {
      credentialId: string;
      datasetId: string;
    }) => {
      return deleteDataset(credentialId, datasetId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['credentials'],
      });
    },
  });
};
